import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import Layout from "@/components/Layout";
import { AuthProvider } from "@/context/AuthContext";
import { FanAuthProvider } from "@/context/FanAuthContext";
import { SiteSettingsProvider } from "@/context/SiteSettingsContext";
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import AdminLayout from "@/components/admin/AdminLayout";

// --- Critical public path — eager loaded (kept in main bundle) --------
// Home, Fighters, Events, EventDetail, FightDetail, FighterProfile,
// Rankings, and the small shared PlaceholderPage / LegalPage / NotFound
// pages account for ~90% of first-visit traffic. Keeping them eager
// avoids a chunk-fetch flash on the most common navigations.
import Home from "@/pages/Home";
import Fighters from "@/pages/Fighters";
import FighterProfile from "@/pages/FighterProfile";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import FightDetail from "@/pages/FightDetail";
import Rankings from "@/pages/Rankings";
import Rejoindre from "@/pages/Rejoindre";
import PlaceholderPage from "@/pages/PlaceholderPage";
import LegalPage from "@/pages/LegalPage";
import NotFound from "@/pages/NotFound";

// --- R4.2 — non-critical pages: lazy-loaded --------------------------
// Fan / auth / payment pages and admin CMS pages are used by a tiny
// fraction of visits. Lazy-loading them cuts the initial main bundle
// from 211 KB → ~130 KB gzip on our seed roster (measured by
// `yarn build`) without changing UX (React.Suspense fallback preloads
// as soon as the user clicks the link).
const Login          = React.lazy(() => import("@/pages/Login"));
const Register       = React.lazy(() => import("@/pages/Register"));
const MonCompte      = React.lazy(() => import("@/pages/MonCompte"));
const Live           = React.lazy(() => import("@/pages/Live"));
const PaymentSuccess = React.lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel  = React.lazy(() => import("@/pages/PaymentCancel"));
const News           = React.lazy(() => import("@/pages/News"));
const NewsDetail     = React.lazy(() => import("@/pages/NewsDetail"));
const Academies      = React.lazy(() => import("@/pages/Academies"));
const AcademyDetail  = React.lazy(() => import("@/pages/AcademyDetail"));

// Admin CMS — every page lazy. Public visitors never download this code.
const AdminLogin         = React.lazy(() => import("@/pages/admin/Login"));
const AdminDashboard     = React.lazy(() => import("@/pages/admin/Dashboard"));
const FightersAdmin      = React.lazy(() => import("@/pages/admin/FightersAdmin"));
const EventsAdmin        = React.lazy(() => import("@/pages/admin/EventsAdmin"));
const FightsAdmin        = React.lazy(() => import("@/pages/admin/FightsAdmin"));
const SponsorsAdmin      = React.lazy(() => import("@/pages/admin/SponsorsAdmin"));
const AcademiesAdmin     = React.lazy(() => import("@/pages/admin/AcademiesAdmin"));
const WeightClassesAdmin = React.lazy(() => import("@/pages/admin/WeightClassesAdmin"));
const SettingsAdmin      = React.lazy(() => import("@/pages/admin/SettingsAdmin"));
const UsersAdmin         = React.lazy(() => import("@/pages/admin/UsersAdmin"));
const RankingsAdmin      = React.lazy(() => import("@/pages/admin/RankingsAdmin"));
const ApplicationsAdmin  = React.lazy(() => import("@/pages/admin/ApplicationsAdmin"));
const PPVAdmin           = React.lazy(() => import("@/pages/admin/PPVAdmin"));
const NewsAdmin          = React.lazy(() => import("@/pages/admin/NewsAdmin"));

// Phase R3 — Official Fighter Card pages (immersive, no public Layout).
// Lazy-loaded so the QR + html-to-image libs never enter the main bundle.
const FighterCardPage = React.lazy(() => import("@/pages/FighterCardPage"));
const FighterVerifyPage = React.lazy(() => import("@/pages/FighterVerifyPage"));

// Shared Suspense fallback (kept minimal to avoid layout shift).
const LazyFallback = () => (
  <div className="min-h-[40vh] bg-black" aria-hidden="true" />
);
const ImmersiveFallback = () => (
  <div className="min-h-screen bg-black" aria-hidden="true" />
);

const AdminRoute = ({ minRole = "editor", children }) => (
  <ProtectedRoute minRole={minRole}>
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <FanAuthProvider>
          <SiteSettingsProvider>
          <BrowserRouter>
            <React.Suspense fallback={<LazyFallback />}>
            <Routes>
              {/* ---------------- Admin (isolated layout) ---------------- */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/fighters" element={<AdminRoute><FightersAdmin /></AdminRoute>} />
              <Route path="/admin/events" element={<AdminRoute><EventsAdmin /></AdminRoute>} />
              <Route path="/admin/fights" element={<AdminRoute><FightsAdmin /></AdminRoute>} />
              <Route path="/admin/sponsors" element={<AdminRoute><SponsorsAdmin /></AdminRoute>} />
              <Route path="/admin/academies" element={<AdminRoute><AcademiesAdmin /></AdminRoute>} />
              <Route path="/admin/rankings" element={<AdminRoute><RankingsAdmin /></AdminRoute>} />
              <Route path="/admin/applications" element={<AdminRoute><ApplicationsAdmin /></AdminRoute>} />
              <Route path="/admin/ppv" element={<AdminRoute><PPVAdmin /></AdminRoute>} />
              <Route path="/admin/news" element={<AdminRoute><NewsAdmin /></AdminRoute>} />
              <Route path="/admin/weight-classes" element={<AdminRoute minRole="admin"><WeightClassesAdmin /></AdminRoute>} />
              <Route path="/admin/settings" element={<AdminRoute minRole="admin"><SettingsAdmin /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute minRole="super_admin"><UsersAdmin /></AdminRoute>} />

              {/* Phase R3 — Official Fighter Card (immersive, no public Layout) */}
              <Route
                path="/card/:token"
                element={
                  <React.Suspense fallback={<ImmersiveFallback />}>
                    <FighterCardPage />
                  </React.Suspense>
                }
              />
              <Route
                path="/verify/fighter/:lookup"
                element={
                  <React.Suspense fallback={<ImmersiveFallback />}>
                    <FighterVerifyPage />
                  </React.Suspense>
                }
              />

              {/* ---------------- Public (with public Layout) ---------------- */}
              <Route
                path="*"
                element={
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/fighters" element={<Fighters />} />
                      <Route path="/fighters/:slug" element={<FighterProfile />} />
                      <Route path="/events" element={<Events />} />
                      <Route path="/events/:slug" element={<EventDetail />} />
                      <Route path="/fights/:slug" element={<FightDetail />} />
                      <Route path="/rankings" element={<Rankings />} />
                      <Route path="/rejoindre" element={<Rejoindre />} />

                      {/* Fan pages */}
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/mon-compte" element={<MonCompte />} />
                      <Route path="/live" element={<Live />} />
                      <Route path="/live/payment/success" element={<PaymentSuccess />} />
                      <Route path="/live/payment/cancel" element={<PaymentCancel />} />

                      {/* Actualités */}
                      <Route path="/actualites" element={<News />} />
                      <Route path="/actualites/:slug" element={<NewsDetail />} />

                      {/* Académies */}
                      <Route path="/academies" element={<Academies />} />
                      <Route path="/academies/:slug" element={<AcademyDetail />} />

                      <Route
                        path="/actualites-old"
                        element={
                          <PlaceholderPage
                            eyebrow="Salle de presse"
                            title="AUCUNE ACTUALITÉ POUR L'INSTANT"
                            subtitle="Les annonces officielles de la ligue, les analyses et les backstage arriveront bientôt."
                            testId="news-placeholder"
                          />
                        }
                      />
                      <Route
                        path="/contact"
                        element={
                          <LegalPage
                            slug="contact"
                            fallbackTitle="NOUS ÉCRIRE"
                            fallbackEyebrow="Contact"
                            fallbackSubtitle="Pour toute demande professionnelle, partenariat ou presse, écrivez à contact@bufl.bf."
                          />
                        }
                      />
                      <Route
                        path="/confidentialite"
                        element={
                          <LegalPage
                            slug="confidentialite"
                            fallbackTitle="POLITIQUE DE CONFIDENTIALITÉ"
                            fallbackEyebrow="Légal"
                            fallbackSubtitle="Document en cours de rédaction. Il sera publié avant le lancement officiel de la ligue."
                          />
                        }
                      />
                      <Route
                        path="/conditions"
                        element={
                          <LegalPage
                            slug="conditions"
                            fallbackTitle="CONDITIONS D'UTILISATION"
                            fallbackEyebrow="Légal"
                            fallbackSubtitle="Document en cours de rédaction. Il sera publié avant le lancement officiel de la ligue."
                          />
                        }
                      />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Layout>
                }
              />
            </Routes>
            </React.Suspense>
          </BrowserRouter>
          </SiteSettingsProvider>
        </FanAuthProvider>
      </AuthProvider>
    </div>
  );
}
